import { lazy, Suspense, useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { CrmProvider } from './contexts/CrmContext';
import { BrainProvider } from './contexts/BrainContext';
import { getTenant } from './lib/tenant';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';

// Non-default routes are code-split so their bundles stay out of first paint.
// Login + Dashboard remain eager — they're the surfaces users land on.
const Setup = lazy(() => import('./pages/Setup').then((m) => ({ default: m.Setup })));
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));
const Brain = lazy(() => import('./pages/Brain').then((m) => ({ default: m.Brain })));
const ShopIndex = lazy(() => import('./pages/shop/ShopIndex').then((m) => ({ default: m.ShopIndex })));
const ShopProduct = lazy(() => import('./pages/shop/ShopProduct').then((m) => ({ default: m.ShopProduct })));
const Quote = lazy(() => import('./pages/shop/Quote').then((m) => ({ default: m.Quote })));

function RouteFallback() {
  return (
    <div className="flex h-screen items-center justify-center text-neutral-500">
      Loading…
    </div>
  );
}

type Route = 'login' | 'dashboard' | 'setup' | 'settings' | 'brain' | 'shop' | 'shop-product' | 'quote';

interface ParsedRoute {
  route: Route;
  productSlug?: string;
}

function parseHashRoute(): ParsedRoute {
  // Strip leading `#`, then split off both `/` (sub-routes) and `?` (query hints
  // from OAuth callbacks like `#setup?step=suburb-sheet&google=ok`).
  const raw = window.location.hash.replace(/^#/, '');
  const beforeQuery = raw.split('?')[0];
  const segments = beforeQuery.split('/').filter(Boolean);
  const h = segments[0];
  if (h === 'dashboard') return { route: 'dashboard' };
  if (h === 'setup') return { route: 'setup' };
  if (h === 'settings') return { route: 'settings' };
  if (h === 'brain') return { route: 'brain' };
  if (h === 'quote') return { route: 'quote' };
  if (h === 'shop') {
    if (segments[1]) return { route: 'shop-product', productSlug: segments[1] };
    return { route: 'shop' };
  }
  return { route: 'login' };
}

// Routes that are PUBLIC — no auth/tenant gate. Shop + quote-builder are the
// marketing surface, so anonymous visitors can browse and request a quote.
const PUBLIC_ROUTES: Route[] = ['shop', 'shop-product', 'quote'];

function Router() {
  const { session, loading: authLoading } = useAuth();
  const { tenant, tenantError, membership, loading: tenantLoading } = useTenant();
  const [parsed, setParsed] = useState<ParsedRoute>(parseHashRoute());
  const { route, productSlug } = parsed;

  useEffect(() => {
    const onHash = () => setParsed(parseHashRoute());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const isPublic = PUBLIC_ROUTES.includes(route);

  useEffect(() => {
    if (authLoading || tenantLoading) return;
    if (isPublic) return;
    if (!session && route !== 'login') {
      window.location.hash = '#login';
    } else if (session && route === 'login') {
      window.location.hash = '#dashboard';
    }
  }, [session, route, isPublic, authLoading, tenantLoading]);

  // Public shop routes render WITHOUT auth/tenant gating — they're the marketing
  // surface. Skip auth-loading + tenant-error guards so anonymous visitors get
  // straight to the grid.
  if (isPublic) {
    if (route === 'shop-product' && productSlug) {
      return (
        <Suspense fallback={<RouteFallback />}>
          <ShopProduct slug={productSlug} />
        </Suspense>
      );
    }
    if (route === 'quote') {
      return (
        <Suspense fallback={<RouteFallback />}>
          <Quote />
        </Suspense>
      );
    }
    return (
      <Suspense fallback={<RouteFallback />}>
        <ShopIndex />
      </Suspense>
    );
  }

  if (authLoading || tenantLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-neutral-500">
        Loading…
      </div>
    );
  }

  if (tenantError) {
    return (
      <div className="flex h-screen items-center justify-center px-8">
        <div className="panel max-w-md text-center">
          <div className="text-xs font-medium uppercase tracking-widest text-red-400">Tenant error</div>
          <h1 className="mt-2 text-xl font-semibold">Can't resolve this admin site.</h1>
          <p className="mt-3 text-sm text-neutral-400">{tenantError}</p>
          <p className="mt-4 text-xs text-neutral-500">Check <code className="rounded bg-white/10 px-1 py-0.5">VITE_TENANT_SLUG</code> and the <code className="rounded bg-white/10 px-1 py-0.5">v9_tenants</code> table.</p>
        </div>
      </div>
    );
  }

  if (!tenant) return null;
  if (!session) return <Login />;

  // /#brain — owner/admin only. Anyone else lands on the dashboard.
  if (route === 'brain') {
    const role = membership?.role;
    if (role === 'owner' || role === 'admin') {
      return (
        <Suspense fallback={<RouteFallback />}>
          <Brain />
        </Suspense>
      );
    }
    window.location.hash = '#dashboard';
    return null;
  }

  if (route === 'setup') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Setup />
      </Suspense>
    );
  }
  if (route === 'settings') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Settings />
      </Suspense>
    );
  }
  return <Dashboard />;
}

export default function App() {
  const tenant = getTenant();
  useEffect(() => {
    document.documentElement.style.setProperty('--brand', tenant.tenant.primaryColor);
  }, [tenant]);
  return (
    <AuthProvider>
      <TenantProvider>
        <CrmProvider>
          <BrainProvider>
            <Router />
          </BrainProvider>
        </CrmProvider>
      </TenantProvider>
    </AuthProvider>
  );
}
